import { oktaAuth } from '../config/oktaConfig';
import { getDefaultStore } from 'jotai';
import { userAtom } from '~/atoms/user';
import { PROFILE_SAVED_VIEW_MAPPING } from '~/constants/profileSavedViewMapping';
import { createProfileSavedViewKey } from '~/utils/profileSavedViewUtils';
import log from '../logger';
const store = getDefaultStore();
const getWorkspaceConfig = () => {
    const config = {
        baseUrl: process.env.REACT_APP_WORKSPACE_API_BASE_URL || '',
        namespace: process.env.REACT_APP_WORKSPACE_NAMESPACE || ''
    };
    if (!config.baseUrl)
        log.warn('REACT_APP_WORKSPACE_API_BASE_URL is empty');
    if (!config.namespace)
        log.warn('REACT_APP_WORKSPACE_NAMESPACE is empty');
    return config;
};
export const fetchUserPreferences = async () => {
    try {
        const user = await store.get(userAtom);
        if (!user?.email) {
            log.warn('No user email available, returning default preferences');
            return {
                brand: '',
                division: '',
                fieldsOfPlay: ''
            };
        }
        const apiPreferences = await fetchUserPreferencesFromApi();
        return {
            brand: apiPreferences.brand || '',
            division: apiPreferences.division || '',
            fieldsOfPlay: apiPreferences.fieldsOfPlay || ''
        };
    }
    catch (error) {
        log.error('Error in fetchUserPreferences:', error);
        return {
            brand: '',
            division: '',
            fieldsOfPlay: ''
        };
    }
};
export const fetchUserPreferencesFromApi = async () => {
    const { baseUrl, namespace } = getWorkspaceConfig();
    try {
        const accessToken = oktaAuth.getAccessToken();
        if (!accessToken) {
            throw new Error('No access token available. User may not be authenticated.');
        }
        const apiUrl = `${baseUrl}/preferences/v1/${namespace}/profile`;
        const response = await fetch(apiUrl, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                Accept: 'application/json'
            }
        });
        if (response.status === 404) {
            log.info('No user preferences found, returning empty object');
            return {};
        }
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }
        const data = (await response.json());
        const profile = 'profile' in data
            ? data.profile
            : data;
        return {
            brand: profile.brand,
            division: profile.division,
            fieldsOfPlay: profile.fields_of_play
        };
    }
    catch (error) {
        log.error('Error fetching user preferences:', error);
        throw error;
    }
};
export const saveUserPreferences = async (preferences) => {
    const { baseUrl, namespace } = getWorkspaceConfig();
    try {
        const accessToken = oktaAuth.getAccessToken();
        if (!accessToken) {
            throw new Error('No access token available. User may not be authenticated.');
        }
        const apiUrl = `${baseUrl}/preferences/v1/${namespace}/profile`;
        const payload = {
            brand: preferences.brand,
            division: preferences.division,
            fields_of_play: preferences.fieldsOfPlay
        };
        const response = await fetch(apiUrl, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            body: JSON.stringify(payload)
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }
        log.info('Successfully saved user preferences');
    }
    catch (error) {
        log.error('Error saving user preferences:', error);
        throw error;
    }
};
/**
 * Set user's default view by calling the workspace API
 */
export async function setUserDefaultView(projectId) {
    try {
        const accessToken = oktaAuth.getAccessToken();
        if (!accessToken) {
            throw new Error('No access token available. User may not be authenticated.');
        }
        const { baseUrl, namespace } = getWorkspaceConfig();
        const apiUrl = `${baseUrl}/preferences/v1/${namespace}/defaultView`;
        const requestBody = {
            defaultView: projectId
        };
        const response = await fetch(apiUrl, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            body: JSON.stringify(requestBody)
        });
        if (!response.ok) {
            throw new Error(`Failed to set default view. HTTP error! status: ${response.status} - ${response.statusText}`);
        }
        log.info('Successfully set user default view', {
            projectId,
            status: response.status
        });
    }
    catch (error) {
        log.error('Failed to set user default view', {
            projectId,
            error: error instanceof Error ? error.message : 'Unknown error'
        });
        throw error;
    }
}
/**
 * MAIN FUNCTION: Get project ID based on user profile (division, brand, field of play)
 * This is the primary function to call when you need to get a projectId from profile values
 *
 * @param division - User's division (e.g., 'Accessories', 'All', 'Equipment')
 * @param brand - User's brand (e.g., 'Nike', 'Nike Air', 'Jordan')
 * @param fieldOfPlay - User's field of play (e.g., 'Jordan Sport', 'Mens Basketball', 'Womens Lifestyle')
 * @returns projectId (UUID) or null if no mapping found
 *
 * @example
 * const projectId = getProfileProjectId('Accessories', 'Nike', 'Jordan Sport')
 */
export function getProfileProjectId(division, brand, fieldOfPlay) {
    const key = createProfileSavedViewKey(division, brand, fieldOfPlay);
    const projectId = PROFILE_SAVED_VIEW_MAPPING[key]?.projectId;
    if (projectId) {
        log.info('Profile project ID found', {
            division,
            brand,
            fieldOfPlay,
            projectId
        });
        return projectId;
    }
    const defaultProjectId = PROFILE_SAVED_VIEW_MAPPING['*|*|*']?.projectId ?? null;
    if (defaultProjectId) {
        log.info('Using default project ID for profile', {
            division,
            brand,
            fieldOfPlay,
            projectId: defaultProjectId
        });
    }
    else {
        log.warn('No project ID found for profile', {
            division,
            brand,
            fieldOfPlay
        });
    }
    return defaultProjectId;
}
/**
 * Get complete saved view mapping based on user profile
 */
export function getProfileSavedViewMapping(division, brand, fieldOfPlay) {
    const key = createProfileSavedViewKey(division, brand, fieldOfPlay);
    const mapping = PROFILE_SAVED_VIEW_MAPPING[key];
    if (mapping) {
        log.info('Profile saved view mapping found', {
            division,
            brand,
            fieldOfPlay,
            projectId: mapping.projectId
        });
        return mapping;
    }
    const defaultMapping = PROFILE_SAVED_VIEW_MAPPING['*|*|*'] ?? null;
    if (defaultMapping) {
        log.info('Using default saved view mapping for profile', {
            division,
            brand,
            fieldOfPlay,
            projectId: defaultMapping.projectId
        });
    }
    else {
        log.warn('No saved view mapping found for profile', {
            division,
            brand,
            fieldOfPlay
        });
    }
    return defaultMapping;
}
/**
 * Apply default saved view based on user profile
 * This function should be called when user updates their profile preferences
 */
export async function applyDefaultSavedViewForProfile(division, brand, fieldOfPlay) {
    try {
        const mapping = getProfileSavedViewMapping(division, brand, fieldOfPlay);
        if (!mapping) {
            log.warn('Cannot apply default saved view - no mapping found', {
                division,
                brand,
                fieldOfPlay
            });
            return null;
        }
        await setUserDefaultView(mapping.projectId);
        log.info('Default saved view applied for user profile', {
            division,
            brand,
            fieldOfPlay,
            projectId: mapping.projectId
        });
        return mapping.projectId;
    }
    catch (error) {
        log.error('Failed to apply default saved view for profile', {
            division,
            brand,
            fieldOfPlay,
            error: error instanceof Error ? error.message : 'Unknown error'
        });
        throw error;
    }
}
